import { useState, useCallback, createContext } from "react";
import { apiBaseurl } from "../utils/ApiBaseurl";
import { useToast } from "./ToastContextHelper";

export const EmployeeManagementContext = createContext();

export const EmployeeManagementProvider = ({ children }) => {
  const { setToastMessage } = useToast();
  const [userEmployees, setUserEmployees] = useState([]);
  const [userEmployee, setUserEmployee] = useState("");
  const [companyProfile, setCompanyProfile] = useState("");

  const getUserEmployees = useCallback(async () => {
    try {
      const response = await fetch(
        `${apiBaseurl}backend/api/employee-management`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${
              JSON.parse(localStorage.getItem("authTokens")).access
            }`,
            "Content-Type": "application/json",
          },
        }
      );

      const _userEmployees = await response.json();
      setUserEmployees(_userEmployees);
    } catch (error) {
      console.error("Failed to fetch user employees:", error);
    }
  }, []);

  const getUserEmployee = useCallback(async (employeeId) => {
    try {
      const response = await fetch(
        `${apiBaseurl}backend/api/employee-management?employee_id=${employeeId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${
              JSON.parse(localStorage.getItem("authTokens")).access
            }`,
            "Content-Type": "application/json",
          },
        }
      );

      const _userEmployee = await response.json();
      setUserEmployee(_userEmployee);
    } catch (error) {
      console.error("Failed to fetch user employee:", error);
    }
  }, []);

  const getCompanyProfile = useCallback(async () => {
    try {
      const response = await fetch(`${apiBaseurl}backend/api/company-profile`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${
            JSON.parse(localStorage.getItem("authTokens")).access
          }`,
          "Content-Type": "application/json",
        },
      });

      const _companyProfile = await response.json();
      setCompanyProfile(_companyProfile);
    } catch (error) {
      console.error("Failed to fetch company profile:", error);
    }
  }, []);

  const createEmployee = useCallback(
    async (
      firstName,
      middleName,
      lastName,
      dateOfBirth,
      ssn,
      email,
      phoneNumber,
      address,
      city,
      state,
      zipcode,
      jobTitle,
      vestingSchedule,
      hireDate,
      payRate,
      payPeriod,
      annualSalary,
      employmentStatus,
      terminationDate
    ) => {
      try {
        const response = await fetch(
          `${apiBaseurl}backend/api/employee-management`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${
                JSON.parse(localStorage.getItem("authTokens")).access
              }`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              first_name: firstName,
              middle_name: middleName,
              last_name: lastName,
              encrypted_dob: dateOfBirth,
              encrypted_ssn: ssn,
              email: email,
              phone: phoneNumber,
              address: address,
              city: city,
              state: state,
              zip_code: zipcode,
              vesting_schedule: vestingSchedule,
              job_title: jobTitle,
              hire_date: hireDate,
              pay_rate: payRate,
              pay_period: payPeriod,
              annual_salary: annualSalary,
              employment_status: employmentStatus,
              termination_date: terminationDate,
            }),
          }
        );
        if (response.ok) {
          await getUserEmployees();
          setToastMessage("Employee created successfully!");
        } else {
          const errors = await response.json();
          errors.forEach((error) => {
            setToastMessage(error);
          });
        }
      } catch (error) {
        console.error("Error creating employee:", error);
      }
    },
    []
  );

  const editEmployee = useCallback(
    async (
      employeeId,
      firstName,
      middleName,
      lastName,
      dateOfBirth,
      ssn,
      email,
      phoneNumber,
      address,
      city,
      state,
      zipcode,
      jobTitle,
      vestingSchedule,
      hireDate,
      payRate,
      payPeriod,
      annualSalary,
      employmentStatus,
      terminationDate
    ) => {
      try {
        const response = await fetch(
          `${apiBaseurl}backend/api/employee-management?employee_id=${employeeId}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${
                JSON.parse(localStorage.getItem("authTokens")).access
              }`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              first_name: firstName,
              middle_name: middleName,
              last_name: lastName,
              encrypted_dob: dateOfBirth,
              encrypted_ssn: ssn,
              email: email,
              phone: phoneNumber,
              address: address,
              city: city,
              state: state,
              zip_code: zipcode,
              vesting_schedule: vestingSchedule,
              job_title: jobTitle,
              hire_date: hireDate,
              pay_rate: payRate,
              pay_period: payPeriod,
              annual_salary: annualSalary,
              employment_status: employmentStatus,
              termination_date: terminationDate,
            }),
          }
        );
        if (response.ok) {
          await getUserEmployees();
          setToastMessage("Employee updated successfully!");
        } else {
          const errors = await response.json();
          errors.forEach((error) => {
            setToastMessage(error);
          });
        }
      } catch (error) {
        console.error("Error updating employee:", error);
      }
    },
    []
  );

  const deleteEmployee = useCallback(async (employeeId) => {
    try {
      const response = await fetch(
        `${apiBaseurl}backend/api/employee-management?employee_id=${employeeId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${
              JSON.parse(localStorage.getItem("authTokens")).access
            }`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.ok) {
        await getUserEmployees();
        setToastMessage("Employee deleted successfully!");
      } else {
        const errors = await response.json();
        errors.forEach((error) => {
          setToastMessage(error);
        });
      }
    } catch (error) {
      console.error("Error updating employee:", error);
    }
  }, []);

  return (
    <EmployeeManagementContext.Provider
      value={{
        userEmployee,
        userEmployees,
        companyProfile,
        getUserEmployee,
        getUserEmployees,
        createEmployee,
        editEmployee,
        setUserEmployee,
        getCompanyProfile,
        deleteEmployee
      }}
    >
      {children}
    </EmployeeManagementContext.Provider>
  );
};
